import { Icon } from '@iconify/react/dist/iconify.js';
import { useState } from 'react';
import { createTicket, fetchContactsByAccount, createContact } from '../../api/ticketData';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { storePendingParentChange, storePendingChildChange, createChangeData } from '../../api/equipmentApi';
import { useEquipment } from '../../contexts/EquipmentContext';

// Display field component for review (no editing)
const ReviewField = ({ label, value, originalValue }) => {
    const hasChanged = value !== originalValue;
    
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="text-xs text-font-secondary font-medium">{label}</label>
                {hasChanged && (
                    <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200 flex items-center gap-1">
                        <Icon icon="icon-park-outline:switch" className="text-green-600 text-sm" />
                        Modified
                    </div>
                )}
            </div>
            <p className="text-sm text-font-primary">{value || 'N/A'}</p>
            
            {hasChanged && (
                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-200">
                    <strong>Original:</strong> {originalValue || 'N/A'}
                </div>
            )}
        </div>
    );
};

const ReviewEquipment = ({ onBack, onSuccess, parentChanges, childChanges, selectedParent, selectedChild }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    // No longer need toggle - always do both operations
    const user = useAuthUser();
    const { refreshData } = useEquipment();

    // Get or create contact in the specified account (Wards)
    const getOrCreateContact = async (email, accountId) => {
        try {
            // First, try to find existing contact in this account
            const contacts = await fetchContactsByAccount(accountId);
            const existingContact = contacts.find(
                (c) => c.email === email && (c.accountId === accountId || c.account?.id === accountId)
            );

            if (existingContact) {
                console.log('ReviewEquipment: Found existing contact:', existingContact.id || existingContact.contactId);
                return existingContact.id || existingContact.contactId;
            }

            // Contact doesn't exist - create it in the specified account
            console.log('ReviewEquipment: Creating new contact in account:', accountId);
            const emailParts = email.split('@')[0].split('.');
            const firstName = emailParts[0] || 'Contact';
            const lastName = emailParts[1] || 'User';

            const newContact = await createContact({
                email: email,
                accountId: accountId,
                firstName: firstName,
                lastName: lastName
            });

            console.log('ReviewEquipment: Created new contact:', newContact.id || newContact.contactId);
            return newContact.id || newContact.contactId;
        } catch (error) {
            console.error('ReviewEquipment: Error getting/creating contact:', error);
            throw new Error(`Failed to get or create contact: ${error.message}`);
        }
    };



    // Handle submission - do both Cosmos DB storage AND ticket creation
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            const userEmail = user?.email || 'unknown@example.com';
            const customerId = 'wards'; // For now, hardcoded to wards
            
            // Step 1: Store changes in Cosmos DB (if there are any changes)
            if (Object.keys(parentChanges).length > 0 || Object.keys(childChanges).length > 0) {
                console.log('🔄 Storing changes in Cosmos DB...');
                
                // Store parent changes if any
                if (Object.keys(parentChanges).length > 0 && selectedParent) {
                    const modifiedParentData = { ...selectedParent };
                    Object.keys(parentChanges).forEach(key => {
                        modifiedParentData[key] = parentChanges[key];
                    });

                    const parentChangeData = createChangeData(
                        selectedParent,
                        modifiedParentData,
                        userEmail,
                        'update'
                    );

                    parentChangeData.parentEqpId = selectedParent.parentEqpId;
                    parentChangeData.changeType = 'parent';

                    console.log('Storing parent change:', parentChangeData);
                    await storePendingParentChange(customerId, parentChangeData);
                }

                // Store child changes if any
                if (Object.keys(childChanges).length > 0 && selectedChild) {
                    const modifiedChildData = { ...selectedChild };
                    Object.keys(childChanges).forEach(key => {
                        modifiedChildData[key] = childChanges[key];
                    });

                    const childChangeData = createChangeData(
                        selectedChild,
                        modifiedChildData,
                        userEmail,
                        'update'
                    );

                    childChangeData.parentEqpId = selectedParent.parentEqpId;
                    childChangeData.childEqpId = selectedChild.childEqpId || selectedChild.childCategory;
                    childChangeData.changeType = 'child';

                    console.log('Storing child change:', childChangeData);
                    await storePendingChildChange(customerId, childChangeData);
                }

                console.log('✅ Changes stored in Cosmos DB successfully');
            }

            // Step 2: Create Zoho Desk ticket (always create a ticket for tracking)
            console.log('🔄 Creating Zoho Desk ticket...');
            
            try {
                // Wards account ID
                const wardsAccountId = '1094595000000560001';
                
                // Get or create contact in Wards account for mohit.bansal@helios-core.com
                const assigneeEmail = 'mohit.bansal@helios-core.com';
                console.log('ReviewEquipment: Getting/creating contact for:', assigneeEmail, 'in account:', wardsAccountId);
                
                const contactId = await getOrCreateContact(assigneeEmail, wardsAccountId);
                console.log('ReviewEquipment: Contact ID obtained:', contactId);

                // Build subject
                let subject = `Review Inventory: ${selectedParent?.parentCategory || 'Unknown Category'}`;
                if (Object.keys(childChanges).length > 0 && selectedChild) {
                    subject += ` - ${selectedChild.childCategory || 'Unknown Child'}`;
                }

                // Build description
                let description = `Parent: ${selectedParent?.parentCategory || 'Unknown Category'}<br>`;
                if (Object.keys(childChanges).length > 0 && selectedChild) {
                    description += `Child: ${selectedChild.childCategory || 'Unknown Child'}<br>`;
                }
                description += '<br>-------<br><br>';

                // Add parent changes
                if (Object.keys(parentChanges).length > 0) {
                    description += 'Parent Changes:<br><br>';
                    Object.keys(parentChanges).forEach(fieldKey => {
                        const fieldLabel = fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                        const originalValue = selectedParent[fieldKey] || 'N/A';
                        const newValue = parentChanges[fieldKey];
                        
                        description += `${fieldLabel} - ${originalValue}<br><br>`;
                        description += `Edits: ${newValue}<br><br>`;
                        description += '----<br><br>';
                    });
                }

                // Add child changes
                if (Object.keys(childChanges).length > 0) {
                    description += 'Child Changes:<br><br>';
                    Object.keys(childChanges).forEach(fieldKey => {
                        const fieldLabel = fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                        const originalValue = selectedChild[fieldKey] || 'N/A';
                        const newValue = childChanges[fieldKey];
                        
                        description += `${fieldLabel} - ${originalValue}<br><br>`;
                        description += `Edits: ${newValue}<br><br>`;
                        description += '----<br><br>';
                    });
                }

                // Create ticket data
                const ticketData = {
                    subject: subject,
                    description: description,
                    email: assigneeEmail, // mohit.bansal@helios-core.com
                    contactId: contactId, // Contact ID for mohit.bansal@helios-core.com in Wards account
                    departmentId: '1094595000000006907',
                    classification: 'Problem',
                    priority: 'High',
                    channel: 'Web',
                    language: 'en',
                    accountId: wardsAccountId, // Wards Rental account ID
                };

                console.log('ReviewEquipment: Creating ticket with data:', {
                    accountId: ticketData.accountId,
                    contactId: ticketData.contactId,
                    email: ticketData.email,
                    subject: ticketData.subject
                });

                await createTicket(ticketData);
                console.log('✅ Zoho Desk ticket created successfully');
            } catch (ticketError) {
                console.error('❌ ReviewEquipment: Failed to create ticket:', ticketError);
                // Don't throw - allow the submission to succeed even if ticket creation fails
                // But log the error for debugging
                console.warn('⚠️ ReviewEquipment: Ticket creation failed, but changes were saved to Cosmos DB');
            }

            setSubmitSuccess(true);
            
            // Refresh equipment data to show pending change indicators
            setTimeout(() => {
                refreshData();
            }, 1000);

            // Redirect back to Inventory page after 2 seconds
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess();
                }
            }, 2000);

        } catch (error) {
            console.error('Failed to submit changes:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                stack: error.stack
            });
            setSubmitError(error.message || 'Failed to submit changes');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="responsive-page-layout scrollable">
            {/* Header */}
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-center text-2xl">
                    <Icon icon="mdi:eye" className="text-primary text-3xl"></Icon>
                    Review Changes
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={onBack}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary shadow-neo-tag px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">Back to Edit</span>
                    </button>
                    
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex flex-row gap-2 items-center text-font-primary bg-primary shadow-neo-tag px-3 py-2 rounded-lg text-sm group hover:bg-primary/80 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon 
                            icon="material-symbols:rate-review-rounded" 
                            className="text-xl text-background group-hover:text-background" 
                        />
                        <span className="text-sm font-medium text-background group-hover:text-background">
                            {isSubmitting ? 'Submitting...' : 'Submit Review'}
                        </span>
                    </button>
                </div>
            </div>

            {/* Success/Error Messages */}
            {submitSuccess && (
                <div className="w-full bg-green-900 text-green-300 border border-green-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:check-circle-outline" className="text-xl" />
                        <span className="text-sm">
                            Review submitted successfully!
                        </span>
                    </div>
                    <p className="text-xs text-green-400 mt-1">
                        Changes saved for admin review and ticket created for tracking. Pending changes will be visible with indicators.
                    </p>
                </div>
            )}

            {submitError && (
                <div className="w-full bg-red-900 text-red-300 border border-red-700 rounded-lg p-3 mb-4">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:error-outline" className="text-xl" />
                        <span className="text-sm">{submitError}</span>
                    </div>
                </div>
            )}

            {/* Summary */}
            <div className="review-summary-responsive flex flex-col lg:flex-row items-center justify-between gap-2 w-full bg-bgSecondary shadow-neo-card rounded-lg p-3">
                <div className="flex items-center gap-1">
                    <Icon icon="mdi:information" className="text-primary text-2xl" />
                    <h3 className="text-lg font-semibold text-font-primary">Changes Summary</h3>
                </div>
                <div className="flex flex-row gap-4">
                    <div>
                        <span className="text-font-primary">Parent Changes: </span>
                        <span className="text-primary font-semibold">{Object.keys(parentChanges).length}</span>
                    </div>
                    <div>
                        <span className="text-font-primary">Child Changes: </span>
                        <span className="text-primary font-semibold">{Object.keys(childChanges).length}</span>
                    </div>
                </div>
            </div>

            {/* Fields Container */}
            <div className="review-fields-responsive flex flex-col lg:flex-row w-full gap-4">
                {/* Parent Category Fields */}
                {selectedParent && Object.keys(parentChanges).length > 0 && (
                    <div className={`flex flex-col items-start gap-2 w-full ${Object.keys(childChanges).length > 0 ? 'lg:w-1/2' : 'lg:w-full'}`}>
                        <div className="flex flex-row items-center gap-1 justify-between w-full">
                            <div className="flex flex-row items-center gap-1">
                                <Icon icon="duo-icons:box-2" className="text-2xl text-accent" />
                                <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Parent Details</label>
                            </div>
                            <div className="flex flex-row items-center gap-1">
                                <label className="text-xs text-font-secondary font-medium">Parent ID:</label>
                                <p className="text-sm text-font-primary font-mono font-medium">{selectedParent.parentEqpId || 'N/A'}</p>
                            </div>
                        </div>
                        <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                            {Object.keys(parentChanges).map((fieldKey) => {
                                const fieldLabel = fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                return (
                                    <div key={fieldKey}>
                                        <ReviewField
                                            label={fieldLabel}
                                            value={parentChanges[fieldKey]}
                                            originalValue={selectedParent[fieldKey]}
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent mt-3" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Child Item Fields */}
                {selectedChild && Object.keys(childChanges).length > 0 && (
                    <div className="w-full lg:w-1/2 flex flex-col items-start gap-3">
                        <div className="flex flex-row items-center gap-1 justify-between w-full">
                            <div className="flex flex-row items-center gap-1">
                                <Icon icon="ix:box-open" className="text-2xl text-accent" />
                                <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Child Details</label>
                            </div>
                            <div className="flex flex-row items-center gap-1">
                                <label className="text-xs text-font-secondary font-medium">Child ID:</label>
                                <p className="text-sm text-font-primary font-mono font-medium">{selectedChild.childEqpId || 'N/A'}</p>
                            </div>
                        </div>
                        <div className="w-full rounded-lg p-2 flex flex-col gap-2">
                            {Object.keys(childChanges).map((fieldKey) => {
                                const fieldLabel = fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                return (
                                    <div key={fieldKey}>
                                        <ReviewField
                                            label={fieldLabel}
                                            value={childChanges[fieldKey]}
                                            originalValue={selectedChild[fieldKey]}
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent mt-2" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewEquipment;
